import React, { useEffect, useState, useMemo } from "react";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Check, X, Image as ImageIcon, Download, Filter } from "lucide-react";

const CATS = ["Trasporti", "Pasti", "Alloggio", "Carburante", "Cancelleria", "Altro"];

const statusPill = (s) => {
  const cfg = {
    pending: { bg: "bg-amber-100 text-amber-700", label: "In attesa" },
    approved: { bg: "bg-green-100 text-green-700", label: "Approvato" },
    rejected: { bg: "bg-red-100 text-red-700", label: "Rifiutato" },
  }[s] || { bg: "bg-gray-100 text-gray-600", label: s };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-widest ${cfg.bg}`}>{cfg.label}</span>;
};

export default function ExpensesPage() {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState({ title: "", amount: "", category: "Trasporti", date: new Date().toISOString().slice(0,10), notes: "", receipt_base64: "" });
  const [decideFor, setDecideFor] = useState(null);
  const [decisionStatus, setDecisionStatus] = useState("approved");
  const [decisionNote, setDecisionNote] = useState("");
  const [receiptPreview, setReceiptPreview] = useState(null);

  const load = async () => {
    const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
    const [filtered, full] = await Promise.all([
      api.get(`/expenses${params}`),
      statusFilter === "all" ? Promise.resolve(null) : api.get("/expenses?limit=500"),
    ]);
    setItems(filtered.data);
    setAllItems(full ? full.data : filtered.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, receipt_base64: reader.result });
    reader.readAsDataURL(f);
  };

  const save = async () => {
    if (!form.title || !form.amount) { toast.error("Descrizione e importo obbligatori"); return; }
    try {
      await api.post("/expenses", {
        title: form.title, amount: Number(form.amount), currency: "EUR",
        category: form.category, date: new Date(form.date).toISOString(),
        notes: form.notes, receipt_base64: form.receipt_base64 || null,
      });
      toast.success("Rimborso creato");
      setForm({ title: "", amount: "", category: "Trasporti", date: new Date().toISOString().slice(0,10), notes: "", receipt_base64: "" });
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione"); }
  };

  const openDecide = (exp, status) => { setDecideFor(exp); setDecisionStatus(status); setDecisionNote(""); };
  const submitDecision = async () => {
    try {
      await api.patch(`/expenses/${decideFor.id}`, { status: decisionStatus, note: decisionNote });
      toast.success(decisionStatus === "approved" ? "Rimborso approvato" : "Rimborso rifiutato");
      setDecideFor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const viewReceipt = async (id) => {
    try {
      const r = await api.get(`/expenses/${id}/receipt`);
      if (!r.data.receipt_base64) { toast.info("Nessuno scontrino allegato"); return; }
      setReceiptPreview(r.data);
    } catch { toast.error("Errore caricamento scontrino"); }
  };

  const exportCSV = async () => {
    try {
      const token = localStorage.getItem("tb_token");
      const url = `${API}/expenses/export.csv?status=${statusFilter === "all" ? "all" : statusFilter}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Errore export");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `rimborsi-${statusFilter}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Export completato");
    } catch { toast.error("Errore export"); }
  };

  const stats = useMemo(() => {
    const pending = allItems.filter(i => i.status === "pending");
    const approved = allItems.filter(i => i.status === "approved");
    return {
      pendingCount: pending.length,
      pendingAmount: pending.reduce((a, b) => a + (b.amount || 0), 0),
      approvedCount: approved.length,
      approvedAmount: approved.reduce((a, b) => a + (b.amount || 0), 0),
      total: allItems.length,
    };
  }, [allItems]);

  return (
    <div className="p-4 md:p-12" data-testid="expenses-page">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#757575] mb-2">Modulo Rimborsi Spese</div>
          <h1 className="font-display text-4xl">Note spese</h1>
          <p className="text-[#555] mt-1 text-sm">Workflow approvazione · scontrini allegati · export CSV per paghe</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={exportCSV} data-testid="exp-export-csv" variant="outline" className="rounded-full h-11 border-[#EDE5D8]">
            <Download className="w-4 h-4 mr-2"/> Esporta CSV
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-expense-btn" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00] h-11 btn-glow"><Plus className="w-4 h-4 mr-2"/> Nuova spesa</Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl border-0 max-w-md">
              <DialogHeader><DialogTitle className="font-display text-2xl">Nuovo rimborso</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Descrizione *</Label><Input data-testid="exp-title" className="rounded-xl mt-1" value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})}/></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div><Label>Importo (€) *</Label><Input data-testid="exp-amount" type="number" step="0.01" className="rounded-xl mt-1" value={form.amount} onChange={(e)=>setForm({...form, amount: e.target.value})}/></div>
                  <div><Label>Data</Label><Input type="date" className="rounded-xl mt-1" value={form.date} onChange={(e)=>setForm({...form, date: e.target.value})}/></div>
                </div>
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v)=>setForm({...form, category: v})}>
                    <SelectTrigger className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
                    <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Note</Label><Textarea className="rounded-xl mt-1 min-h-20" value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} placeholder="Es. cliente, motivo, dettagli"/></div>
                <div>
                  <Label>Scontrino (immagine o PDF)</Label>
                  <Input type="file" accept="image/*,application/pdf" onChange={onFile} className="rounded-xl mt-1"/>
                  {form.receipt_base64?.startsWith("data:image/") && <img src={form.receipt_base64} alt="preview" className="mt-2 max-h-32 rounded-xl border border-[#EDE5D8]"/>}
                </div>
              </div>
              <DialogFooter><Button onClick={save} data-testid="exp-save" className="rounded-full bg-[#FF7A29] hover:bg-[#E85D00]">Salva</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI */}
      <div className="grid md:grid-cols-4 gap-4 mb-8">
        <div className="card-smart p-5" data-testid="kpi-pending">
          <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-2">In attesa</div>
          <div className="font-display text-4xl">{stats.pendingCount}</div>
          <div className="text-xs text-[#757575] mt-1">€{stats.pendingAmount.toFixed(2)}</div>
        </div>
        <div className="card-smart p-5" data-testid="kpi-approved">
          <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-2">Approvate</div>
          <div className="font-display text-4xl">{stats.approvedCount}</div>
          <div className="text-xs text-[#757575] mt-1">€{stats.approvedAmount.toFixed(2)}</div>
        </div>
        <div className="card-smart p-5 bg-gradient-to-br from-[#FF7A29] to-[#E85D00] text-white border-0" data-testid="kpi-disbursed">
          <div className="text-[10px] uppercase tracking-widest opacity-80 mb-2">Totale erogato</div>
          <div className="font-display text-4xl">€{stats.approvedAmount.toFixed(2)}</div>
          <div className="text-xs opacity-80 mt-1">{stats.approvedCount} rimborsi</div>
        </div>
        <div className="card-smart p-5 bg-[#0A0A0A] text-white border-[#0A0A0A]" data-testid="kpi-total">
          <div className="text-[10px] uppercase tracking-widest opacity-70 mb-2">Totale richieste</div>
          <div className="font-display text-4xl">{stats.total}</div>
        </div>
      </div>

      {/* Filtri */}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <Filter className="w-4 h-4 text-[#757575]"/>
        {[
          { id: "all", label: "Tutte" },
          { id: "pending", label: "In attesa" },
          { id: "approved", label: "Approvate" },
          { id: "rejected", label: "Rifiutate" },
        ].map((s) => (
          <button key={s.id} onClick={()=>setStatusFilter(s.id)} data-testid={`exp-filter-${s.id}`}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-all ${statusFilter === s.id ? "bg-[#FF7A29] text-white shadow-md shadow-orange-200" : "bg-white border border-[#EDE5D8] hover:border-[#FF7A29]"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Tabella */}
      <div className="card-smart overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-[#EDE5D8]">
            <tr className="text-left">
              <th className="p-4 font-semibold">Data</th>
              <th className="p-4 font-semibold">Dipendente</th>
              <th className="p-4 font-semibold">Descrizione</th>
              <th className="p-4 font-semibold">Categoria</th>
              <th className="p-4 font-semibold">Importo</th>
              <th className="p-4 font-semibold">Stato</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="7" className="p-12 text-center text-[#757575]">Nessuna nota spese con questo filtro.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} className="border-b border-[#EDE5D8] hover:bg-[#FFF0E3]/50" data-testid={`expense-${it.id}`}>
                <td className="p-4 text-xs">{it.date?.slice(0,10)}</td>
                <td className="p-4 font-semibold">{it.user_name}</td>
                <td className="p-4">
                  <div>{it.title}</div>
                  {it.notes && <div className="text-xs text-[#757575]">{it.notes}</div>}
                  {it.decision_note && <div className="text-[10px] italic text-[#757575] mt-1">Risposta: {it.decision_note}</div>}
                </td>
                <td className="p-4 text-xs text-[#757575]">{it.category}</td>
                <td className="p-4 font-display text-base">€{it.amount?.toFixed(2)}</td>
                <td className="p-4">{statusPill(it.status)}</td>
                <td className="p-4 text-right">
                  <Button size="icon" variant="ghost" onClick={()=>viewReceipt(it.id)} data-testid={`view-receipt-${it.id}`} title="Vedi scontrino" className="hover:bg-[#FFF0E3] rounded-full h-8 w-8 mr-1">
                    <ImageIcon className="w-3.5 h-3.5"/>
                  </Button>
                  {it.status === "pending" && (
                    <>
                      <Button size="icon" onClick={()=>openDecide(it, "approved")} data-testid={`approve-${it.id}`} className="bg-green-600 hover:bg-green-700 rounded-full h-8 w-8 mr-1"><Check className="w-4 h-4"/></Button>
                      <Button size="icon" onClick={()=>openDecide(it, "rejected")} data-testid={`reject-${it.id}`} className="bg-red-600 hover:bg-red-700 rounded-full h-8 w-8"><X className="w-4 h-4"/></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog decisione */}
      <Dialog open={!!decideFor} onOpenChange={(v)=>{ if(!v) setDecideFor(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-md" data-testid="exp-decide-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{decisionStatus === "approved" ? "Approva rimborso" : "Rifiuta rimborso"}</DialogTitle></DialogHeader>
          {decideFor && (
            <div className="space-y-4">
              <div className="text-sm bg-[#FFF0E3]/50 border border-[#EDE5D8] rounded-xl p-3">
                <div className="font-semibold">{decideFor.user_name} · {decideFor.title}</div>
                <div className="text-xs text-[#757575]">{decideFor.category} · €{decideFor.amount?.toFixed(2)} · {decideFor.date?.slice(0,10)}</div>
              </div>
              <div><Label>Nota (opzionale)</Label><Input data-testid="exp-decision-note" className="rounded-xl mt-1" value={decisionNote} onChange={(e)=>setDecisionNote(e.target.value)} placeholder="Es: OK, erogato con busta paga di maggio"/></div>
              <DialogFooter>
                <Button onClick={submitDecision} data-testid="exp-decision-submit" className={`rounded-full ${decisionStatus === "approved" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}>
                  Conferma {decisionStatus === "approved" ? "approvazione" : "rifiuto"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog preview scontrino */}
      <Dialog open={!!receiptPreview} onOpenChange={(v)=>{ if(!v) setReceiptPreview(null); }}>
        <DialogContent className="rounded-3xl border-0 max-w-2xl" data-testid="receipt-preview-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Scontrino · {receiptPreview?.title}</DialogTitle></DialogHeader>
          {receiptPreview?.receipt_base64?.startsWith("data:image/") && (
            <img src={receiptPreview.receipt_base64} alt="Scontrino" className="w-full rounded-xl border border-[#EDE5D8]"/>
          )}
          {receiptPreview?.receipt_base64?.startsWith("data:application/pdf") && (
            <iframe src={receiptPreview.receipt_base64} title="Scontrino PDF" className="w-full h-96 rounded-xl border border-[#EDE5D8]"/>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
